import type { RootState } from "@/store"
import { Badge, Box, Button, CircularProgress, IconButton, Slide, Stack, Typography } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { formatarBRL } from "@/lib/formato"
import { atualizarKiosk } from "@/store/self-service-kiosk-slice"
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'

interface AddItemsCartBarProps {
    onConfirm: () => void
    enviando?: boolean
}

function AddItemsCartBar({ onConfirm, enviando = false }: AddItemsCartBarProps): JSX.Element {
    const dispatch = useDispatch()
    const cartItems = useSelector((state: RootState) => state.selfServiceKiosk.cartItems)
    const totalItens = cartItems.reduce((s, i) => s + i.quantidade, 0)
    const totalPreco = cartItems.reduce((s, i) => s + i.valorTotal, 0)
    const abrirCarrinho = (): void => {
        dispatch(atualizarKiosk({ showReviewItems: true }))
    }

    return (
        <Slide direction="up" in={totalItens > 0} mountOnEnter unmountOnExit timeout={300}>
            <Box sx={{ p: 2, pt: 1.5, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                    <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexShrink: 0, px: 1, alignItems: 'center' }}>
                        {/* O enchimento aumenta a área de toque e a margem negativa o desconta: o ícone não sai do lugar. */}
                        <IconButton aria-label="Abrir carrinho" onClick={abrirCarrinho} disabled={enviando} sx={{ p: 1, m: -1 }}>
                            <Badge badgeContent={totalItens} color="primary">
                                <ShoppingCartIcon fontSize="large" color="primary" />
                            </Badge>
                        </IconButton>

                        <Stack spacing={0.5}>
                            <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                                Total: {formatarBRL(totalPreco)}
                            </Typography>

                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                {totalItens} {totalItens === 1 ? 'item' : 'itens'}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={abrirCarrinho}
                            disabled={enviando}
                            sx={{ py: 2, px: 4, borderRadius: 3, textTransform: 'none', flexShrink: 0 }}
                        >
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                <ReceiptLongIcon fontSize="large" />

                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Ver carrinho
                                </Typography>
                            </Stack>
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onConfirm}
                            disabled={enviando}
                            sx={{ py: 2, px: 4, borderRadius: 3, textTransform: 'none' }}
                        >
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                {enviando ? (
                                    <CircularProgress size={35} color="inherit" />
                                ) : (
                                    <AddShoppingCartIcon fontSize="large" />
                                )}

                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {enviando ? 'Adicionando...' : 'Adicionar ao pedido'}
                                </Typography>
                            </Stack>
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Slide>
    )
}

export default AddItemsCartBar